import { useEffect, useMemo, useState } from 'react';
import { getRoleScopes, updateRoleScopes, RoleScopesPayload } from '../lib/api/client';
import { toast } from 'react-hot-toast';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { LoadingSpinner } from '../components/ui/LoadingSpinner';
import { Search, Save, Shield, Settings, CheckCircle } from 'lucide-react';

const ALL_FEATURES: { key: string; label: string }[] = [
  { key: 'dashboard:view', label: 'Dashboard' },
  // Requirements granular
  { key: 'requirements:view', label: 'Requirements - View' },
  { key: 'requirements:add', label: 'Requirements - Add' },
  { key: 'requirements:edit', label: 'Requirements - Edit' },
  { key: 'requirements:delete', label: 'Requirements - Delete' },
  { key: 'requirements:add_candidate', label: 'Requirements - Add Candidate' },
  { key: 'requirements:bulk_upload_candidate', label: 'Requirements - Bulk Upload Candidate' },
  // Candidates
  { key: 'candidates:view', label: 'Candidates - View' },
  { key: 'candidates:add', label: 'Candidates - Add' },
  { key: 'candidates:edit', label: 'Candidates - Edit' },
  { key: 'candidates:delete', label: 'Candidates - Delete' },
  // Clients
  { key: 'clients:view', label: 'Clients - View' },
  { key: 'clients:add', label: 'Clients - Add' },
  { key: 'clients:edit', label: 'Clients - Edit' },
  { key: 'clients:delete', label: 'Clients - Delete' },
  // Audit params
  { key: 'audit_params:view', label: 'Audit Params - View' },
  // User management
  { key: 'user_management:access', label: 'User Management' },
  // Support
  { key: 'support:access', label: 'Support' },
  // Super
  { key: 'all:admin', label: 'All (Ops Head/CEO)' }
];

const ROLES = ['recruiter', 'team_lead', 'manager', 'mis_coordinator', 'ops_head', 'ceo'];

export default function AdminRoleAccess() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [mapping, setMapping] = useState<Record<string, string[]>>({});
  const [filter, setFilter] = useState('');

  const filteredFeatures = useMemo(() => {
    if (!filter.trim()) return ALL_FEATURES;
    const q = filter.toLowerCase();
    return ALL_FEATURES.filter(f => f.label.toLowerCase().includes(q) || f.key.includes(q));
  }, [filter]);

  useEffect(() => {
    (async () => {
      try {
        const data = await getRoleScopes();
        // Initialize with existing permissions marked as checked
        const existingMapping = data.mapping || {};
        
        // Add some sensible defaults if no existing permissions
        const defaultMapping = {
          recruiter: ['dashboard:view', 'requirements:view', 'candidates:view', 'candidates:add', 'support:access'],
          team_lead: ['dashboard:view', 'requirements:view', 'requirements:add', 'requirements:edit', 'candidates:view', 'candidates:add', 'candidates:edit', 'audit_params:view', 'support:access'],
          manager: ['dashboard:view', 'requirements:view', 'requirements:add', 'requirements:edit', 'candidates:view', 'candidates:add', 'candidates:edit', 'clients:view', 'support:access'],
          mis_coordinator: ['dashboard:view', 'requirements:view', 'candidates:view', 'support:access'],
          ops_head: ['all:admin'],
          ceo: ['all:admin']
        };
        
        // Merge existing permissions with defaults (existing takes precedence)
        const mergedMapping = { ...defaultMapping, ...existingMapping };
        setMapping(mergedMapping);
      } catch {
        toast.error('Failed to load role scopes');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const toggle = (role: string, key: string) => {
    setMapping(prev => {
      const current = new Set(prev[role] || []);
      if (current.has(key)) current.delete(key); else current.add(key);
      return { ...prev, [role]: Array.from(current).sort() };
    });
  };

  const selectAll = (role: string) => {
    setMapping(prev => ({ ...prev, [role]: ALL_FEATURES.map(f => f.key) }));
  };

  const clearAll = (role: string) => {
    setMapping(prev => ({ ...prev, [role]: [] }));
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload: RoleScopesPayload = { mapping };
      await updateRoleScopes(payload);
      toast.success('Role scopes updated');
    } catch {
      toast.error('Failed to update role scopes');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-96 flex items-center justify-center">
        <LoadingSpinner size="xl" variant="ai" showText text="Loading role permissions..." />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
            <Shield className="w-6 h-6 text-primary-500" />
            Role Permissions
          </h1>
          <p className="text-gray-600 dark:text-gray-400 text-sm mt-1">
            Configure what each role can access
          </p>
        </div>
        
        <div className="flex gap-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
            <Input
              placeholder="Search features..."
              value={filter}
              onChange={e => setFilter(e.target.value)}
              className="pl-10 w-64"
            />
          </div>
          <Button
            variant="enterprise"
            onClick={save}
            disabled={saving}
            className="min-w-[120px]"
          >
            {saving ? (
              <>
                <LoadingSpinner size="sm" variant="ai" className="mr-2" />
                Saving...
              </>
            ) : (
              <>
                <Save className="w-4 h-4 mr-2" />
                Save
              </>
            )}
          </Button>
        </div>
      </div>

      {/* Permission Matrix Table */}
      <Card className="glass-card overflow-hidden">
        <CardHeader className="border-b border-gray-200 dark:border-gray-700">
          <CardTitle className="flex items-center gap-2 text-gray-900 dark:text-white">
            <Settings className="w-5 h-5 text-primary-500" />
            Role Permissions Matrix
          </CardTitle>
          <CardDescription className="text-gray-600 dark:text-gray-400">
            Click checkboxes to toggle permissions for each role
          </CardDescription>
        </CardHeader>
        <CardContent className="p-0">
          {/* Scrollable Table Container */}
          <div className="overflow-x-auto">
            <table className="w-full">
              {/* Table Header */}
              <thead className="bg-gray-50 dark:bg-gray-800/50 sticky top-0 z-10">
                <tr>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider min-w-[250px]">
                    Feature
                  </th>
                  {ROLES.map(role => (
                    <th key={role} className="px-4 py-4 text-center text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                      <div className="flex flex-col items-center gap-2">
                        <span className="capitalize whitespace-nowrap">
                          {role.replace(/_/g, ' ')}
                        </span>
                        <div className="flex gap-1">
                          <button
                            onClick={() => selectAll(role)}
                            className="text-[10px] px-2 py-0.5 rounded bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300 hover:bg-primary-200 dark:hover:bg-primary-900/50 transition-colors"
                          >
                            All
                          </button>
                          <button
                            onClick={() => clearAll(role)}
                            className="text-[10px] px-2 py-0.5 rounded bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors"
                          >
                            None
                          </button>
                        </div>
                      </div>
                    </th>
                  ))}
                </tr>
              </thead>

              {/* Table Body */}
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                {filteredFeatures.map((feature, idx) => (
                  <tr 
                    key={feature.key}
                    className={`
                      ${idx % 2 === 0 ? 'bg-white dark:bg-gray-900' : 'bg-gray-50 dark:bg-gray-800/30'}
                      hover:bg-primary-50 dark:hover:bg-primary-900/10 transition-colors
                    `}
                  >
                    {/* Feature Name */}
                    <td className="px-6 py-4">
                      <div className="flex flex-col gap-1">
                        <span className="font-medium text-sm text-gray-900 dark:text-white">
                          {feature.label}
                        </span>
                        <span className="text-xs text-gray-500 dark:text-gray-400 font-mono">
                          {feature.key}
                        </span>
                      </div>
                    </td>

                    {/* Role Checkboxes */}
                    {ROLES.map(role => {
                      const checked = (mapping[role] || []).includes(feature.key) || (mapping[role] || []).includes('all:admin');
                      const disabled = (mapping[role] || []).includes('all:admin') && feature.key !== 'all:admin';
                      
                      return (
                        <td key={role} className="px-4 py-4 text-center">
                          <div className="flex justify-center">
                            <button
                              onClick={() => !disabled && toggle(role, feature.key)}
                              disabled={disabled}
                              className={`
                                w-8 h-8 rounded-md border-2 flex items-center justify-center transition-all duration-200
                                ${checked 
                                  ? 'bg-primary-500 border-primary-500 text-white shadow-sm' 
                                  : 'bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600 text-gray-400 hover:border-primary-400 dark:hover:border-primary-500'
                                }
                                ${disabled 
                                  ? 'opacity-40 cursor-not-allowed' 
                                  : 'hover:scale-110 active:scale-95 cursor-pointer hover:shadow-md'
                                }
                              `}
                              title={disabled ? 'Admin role has all permissions' : `Toggle ${feature.label} for ${role}`}
                            >
                              {checked && <CheckCircle className="w-4 h-4" strokeWidth={2.5} />}
                            </button>
                          </div>
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>

            {/* Empty State */}
            {filteredFeatures.length === 0 && (
              <div className="text-center py-16 bg-white dark:bg-gray-900">
                <Search className="w-12 h-12 text-gray-400 dark:text-gray-600 mx-auto mb-4" />
                <p className="text-gray-600 dark:text-gray-400 font-medium">No features found matching your search.</p>
                <button
                  onClick={() => setFilter('')}
                  className="mt-4 text-sm text-primary-600 dark:text-primary-400 hover:underline"
                >
                  Clear search
                </button>
              </div>
            )}
          </div>

          {/* Footer Stats */}
          <div className="border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50 px-6 py-4">
            <div className="flex items-center justify-between text-xs text-gray-600 dark:text-gray-400">
              <div className="flex items-center gap-6">
                <span>Total Features: <strong className="text-gray-900 dark:text-white">{ALL_FEATURES.length}</strong></span>
                {filter && (
                  <span>Filtered: <strong className="text-gray-900 dark:text-white">{filteredFeatures.length}</strong></span>
                )}
              </div>
              <div className="flex items-center gap-2">
                <div className="flex items-center gap-1">
                  <div className="w-3 h-3 rounded bg-primary-500"></div>
                  <span>Enabled</span>
                </div>
                <div className="flex items-center gap-1 ml-4">
                  <div className="w-3 h-3 rounded border-2 border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800"></div>
                  <span>Disabled</span>
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
